// src/components/communication/ChannelsConfigPanel.js
import React, { useState, useEffect } from "react";
import {
  Megaphone,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Check,
  X,
  Factory,
  Wrench,
  Truck,
  ShoppingBag,
  Mail,
  User,
  Layers,
  Shield,
  MessageSquare,
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { chatService, DEFAULT_SYSTEM_CHANNELS } from "../../services/chatService";

const CHANNEL_ICONS = {
  Megaphone: Megaphone,
  Factory: Factory,
  Wrench: Wrench,
  Truck: Truck,
  ShoppingBag: ShoppingBag,
  Mail: Mail,
  User: User,
  Layers: Layers,
};

const MODULE_OPTIONS = [
  { value: "", label: "Geral (Público para todos)" },
  { value: "production", label: "Produção & Fábrica" },
  { value: "oficina", label: "Oficina & Bancada" },
  { value: "orders", label: "Pedidos & Logística" },
  { value: "pdv", label: "PDV Showroom" },
  { value: "postagem", label: "Expedição Correios" },
  { value: "colaboradores", label: "Colaboradores & RH" },
  { value: "stock", label: "Estoque" },
  { value: "financeiro", label: "Financeiro" },
];

export default function ChannelsConfigPanel() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form states
  const [displayName, setDisplayName] = useState("");
  const [name, setName] = useState("");
  const [moduleKey, setModuleKey] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("Megaphone");
  const [saving, setSaving] = useState(false);

  const loadChannels = async () => {
    setLoading(true);
    try {
      const records = await pb.collection("chat_channels").getFullList({
        filter: "type = 'channel'",
        sort: "ordem,name",
      });
      setChannels(records);
    } catch (e) {
      console.error("Erro ao carregar canais:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChannels();
  }, []);

  const handleEdit = (ch) => {
    setEditingId(ch.id);
    setDisplayName(ch.display_name || "");
    setName(ch.name || "");
    setModuleKey(ch.module_key || "");
    setDescription(ch.description || "");
    setIcon(ch.icon || "Megaphone");
  };

  const handleCancel = () => {
    setEditingId(null);
    setDisplayName("");
    setName("");
    setModuleKey("");
    setDescription("");
    setIcon("Megaphone");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) return;

    setSaving(true);
    try {
      const cleanName = (name || displayName)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9_-]/g, "-");

      const payload = {
        name: cleanName,
        display_name: displayName.trim(),
        type: "channel",
        description: description.trim(),
        icon: icon || "Megaphone",
        is_private: false,
      };

      if (moduleKey) {
        payload.module_key = moduleKey;
      } else {
        payload.module_key = "";
      }

      if (editingId) {
        await pb.collection("chat_channels").update(editingId, payload);
      } else {
        await pb.collection("chat_channels").create(payload);
      }

      handleCancel();
      loadChannels();
    } catch (err) {
      console.error("Erro ao salvar canal:", err);
      alert("Erro ao salvar canal: " + (err?.data?.message || err?.message || "Verifique o PocketBase"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este canal de chat?")) return;
    try {
      await pb.collection("chat_channels").delete(id);
      loadChannels();
    } catch (err) {
      console.error("Erro ao deletar canal:", err);
      alert("Erro ao excluir canal.");
    }
  };

  const handleSeedDefaults = async () => {
    setLoading(true);
    try {
      await chatService.seedDefaultChannels();
      await loadChannels();
    } catch (err) {
      console.error("Erro ao seedar:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* HEADER DO PAINEL */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <MessageSquare size={18} className="text-indigo-600" />
            Canais de Comunicação & Chat por Área
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Configure os canais disponíveis para a equipe e vincule permissões automáticas por área.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSeedDefaults}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition cursor-pointer"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            <span>Restaurar Canais Padrão</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* FORMULÁRIO DE CRIAÇÃO / EDIÇÃO */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm h-fit">
          <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
            {editingId ? <Edit2 size={14} className="text-indigo-600" /> : <Plus size={14} className="text-indigo-600" />}
            {editingId ? "Editar Canal" : "Novo Canal de Chat"}
          </h4>

          <form onSubmit={handleSave} className="space-y-3.5">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Nome de Exibição *
              </label>
              <input
                type="text"
                required
                placeholder="Ex: Produção & Fábrica"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Área / Módulo Vinculado (Permissão)
              </label>
              <select
                value={moduleKey}
                onChange={(e) => setModuleKey(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {MODULE_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-400 mt-1">
                Se vazio, o canal é geral (todos os usuários logados podem ver).
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Descrição
              </label>
              <input
                type="text"
                placeholder="Ex: Avisos de fundição e modelagem"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Ícone
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {Object.keys(CHANNEL_ICONS).map((iconName) => {
                  const IconC = CHANNEL_ICONS[iconName];
                  const isSel = icon === iconName;
                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => setIcon(iconName)}
                      className={`p-2 rounded-xl border flex items-center justify-center transition cursor-pointer ${
                        isSel
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                          : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <IconC size={16} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer"
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition disabled:opacity-50 cursor-pointer"
              >
                {saving ? "Salvando..." : editingId ? "Salvar Alterações" : "Criar Canal"}
              </button>
            </div>
          </form>
        </div>

        {/* LISTA DE CANAIS CADASTRADOS */}
        <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center justify-between">
            <span>Canais Cadastrados ({channels.length})</span>
          </h4>

          {loading ? (
            <div className="py-12 text-center text-xs text-slate-400">Carregando canais...</div>
          ) : channels.length === 0 ? (
            <div className="py-12 text-center text-slate-400 space-y-3">
              <Megaphone size={32} className="mx-auto text-slate-300" />
              <p className="text-xs font-semibold">Nenhum canal cadastrado no banco.</p>
              <button
                onClick={handleSeedDefaults}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 shadow-sm transition cursor-pointer"
              >
                Restaurar Canais Padrão Agora
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {channels.map((ch) => {
                const IconComp = CHANNEL_ICONS[ch.icon] || Megaphone;
                const moduleLabel =
                  MODULE_OPTIONS.find((m) => m.value === ch.module_key)?.label ||
                  (ch.module_key ? `Módulo: ${ch.module_key}` : "Geral (Público)");

                return (
                  <div
                    key={ch.id}
                    className="p-3.5 rounded-xl border border-slate-200/90 hover:border-slate-300 bg-white flex items-center justify-between gap-3 transition shadow-xs"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                        <IconComp size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs text-slate-800 truncate">
                            {ch.display_name}
                          </span>
                          <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">
                            {moduleLabel}
                          </span>
                        </div>
                        {ch.description && (
                          <p className="text-xs text-slate-500 truncate mt-0.5">{ch.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleEdit(ch)}
                        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition cursor-pointer"
                        title="Editar Canal"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(ch.id)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                        title="Excluir Canal"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
